import type React from 'react';
import { type FC, useMemo, useCallback } from 'react';
import {
  MantineReactTable,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { createFormatPrice } from '@/lib/util/locale';
import { UiButton, UiHStack, UiIconArrowRight, uiStyles, UiTag } from '@/lib/ui';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type account } from '@/api';
import { useDataTableOptions } from '@/registration/hook/useDataTableOptions';
import { TenantState, tenantStateLabels, tenantStateOptions } from '@/api/constant';

dayjs.extend(advancedFormat);

interface DataTableProps {
  data: account.TenantData[]
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
  filters: MRT_ColumnFiltersState
  setFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>
  sorting: MRT_SortingState
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>
  pagination: MRT_PaginationState
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>
  rowCount: number
}

const DataTable: FC<DataTableProps> = ({
  data,
  isLoading,
  isError,
  isFetching,
  refetch,
  filters,
  setFilters,
  sorting,
  setSorting,
  pagination,
  setPagination,
  rowCount,
}) => {
  const navigate = useNavigate();
  const priceLocale = 'en-AU';
  const priceCurrency = 'AUD';
  const { tenantCode } = useRegisterRoute();
  const dataTableOptions = useDataTableOptions<account.TenantData>();
  const formatPrice = useMemo(
    () => { return createFormatPrice({ locale: priceLocale, currency: priceCurrency }); },
    []
  );

  const toTenantInfoPage = useCallback(
    (tenantId: string) => {
      navigate(generatePageUrl('AAAManageTenantInfo', { tenantId, }));
    },
    []
  );

  const columns = useMemo<Array<MRT_ColumnDef<account.TenantData>>>(
    () => {
      return [
        {
          accessorKey: 'id',
          header: 'ID',
          enableColumnFilter: false,
          size: 50,
        },
        {
          accessorKey: 'state',
          header: 'State',
          filterVariant: 'select',
          mantineFilterSelectProps: {
            data: tenantStateOptions.map((stateOption) => {
              return {
                label: stateOption.label,
                value: `${stateOption.value}`,
              };
            }),
          },
          Cell: ({ cell }) => {
            if (cell.getValue()) {
              if (cell.getValue() === TenantState.Active) {
                return (
                  <UiTag color={'green'}>
                    {tenantStateLabels[cell.getValue() as TenantState]}
                  </UiTag>
                );
              }
              if (cell.getValue() === TenantState.Inactive) {
                return (
                  <UiTag color={'red'}>
                    {tenantStateLabels[cell.getValue() as TenantState]}
                  </UiTag>
                );
              }
            }
            return null;
          },
        },
        {
          accessorKey: 'name',
          header: 'Name',
        },
        {
          accessorKey: 'code',
          header: 'Code',
        },
        {
          accessorKey: 'apiEndpoint',
          header: 'Api endpoint',
          enableColumnFilter: false,
        },
        {
          accessorKey: 'createdAt',
          header: 'Created at',
          Cell: ({ cell }) => {
            return cell.getValue() ? dayjs(cell.getValue() as string | number | Date | Dayjs).format('DD/MM/YYYY HH:mm:ss') : null;
          },
          enableColumnFilter: false,
        },
        {
          accessorKey: 'updatedAt',
          header: 'Updated at',
          Cell: ({ cell }) => {
            return cell.getValue() ? dayjs(cell.getValue() as string | number | Date | Dayjs).format('DD/MM/YYYY HH:mm:ss') : null;
          },
          enableColumnFilter: false,
        },
        // {
        //   accessorKey: 'purchasedAt',
        //   header: 'Purchased at',
        //   Cell: ({ cell }) => {
        //     return cell.getValue() ? dayjs(cell.getValue() as string | number | Date | Dayjs).format('hh:mm A Do MMMM Z') : null;
        //   },
        //   filterVariant: 'date-range'
        // },
        // {
        //   accessorKey: 'paymentOption',
        //   header: 'Payment Option',
        //   filterVariant: 'select'
        // },
      ];
    },
    [tenantStateOptions, formatPrice]
  );

  const handleApplyFilter = () => {
    refetch();
  };

  const handleClearFilters = () => {
    setFilters([]);
    setPagination({ pageIndex: 0, pageSize: 10 });
    setSorting([]);
  };

  const table = useMantineReactTable({
    columns,
    data,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: false,
    },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableHiding: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      columnFilters: filters,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
      pagination,
    },
    // displayColumnDefOptions: { 'mrt-row-actions': { size: 96 } },
    enableRowActions: true,
    positionActionsColumn: 'last',
    // enableColumnResizing: true,
    // globalFilterFn: 'contains',
    renderTopToolbarCustomActions: () => {
      return (
        <UiHStack
          spacing={8}
          flexGrow={1}
          justifyContent={'flex-end'}
          px={4}
          // pt={2}
        >
          <UiButton
            size={'sm'}
            variant={'outline'}
            colorScheme={'primary'}
            onClick={handleApplyFilter}
          >
            Apply filter
          </UiButton>
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={handleClearFilters}>
            Clear all filters
          </UiButton>
        </UiHStack>
      );
    },
    renderRowActions: ({ row }) => {
      return (
        <UiHStack
          px={0}
          justifyContent={'flex-start'}
          onClick={() => { return toTenantInfoPage(`${row.original.id}`); }}
          {...uiStyles.hover}
        >
          <UiIconArrowRight size={'2xl'} color={'primary.500'} />
        </UiHStack>
      );
    },
    ...dataTableOptions,
  });

  return <MantineReactTable table={table} />;
};

export default DataTable;
